import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const UsersTable = ({ 
  users, 
  currentUserId,
  onRoleChange, 
  onDeleteUser,
  sortConfig,
  onSort 
}) => {
  const getSortIcon = (column) => {
    if (sortConfig?.column !== column) {
      return <Icon name="ChevronsUpDown" size={16} color="var(--color-muted-foreground)" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={16} color="var(--color-primary)" />
      : <Icon name="ChevronDown" size={16} color="var(--color-primary)" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' }
  ];

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-error/20 text-error';
      case 'editor':
        return 'bg-warning/20 text-warning';
      case 'viewer':
        return 'bg-success/20 text-success';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="overflow-x-auto bg-card border border-border rounded-lg">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border">
            <th 
              className="text-left p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort('email')}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Email</span>
                {getSortIcon('email')}
              </div>
            </th>
            <th 
              className="text-left p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort('fullName')}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Name</span>
                {getSortIcon('fullName')}
              </div>
            </th>
            <th 
              className="text-left p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort('role')}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Role</span>
                {getSortIcon('role')}
              </div>
            </th>
            <th 
              className="text-left p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort('lastLogin')}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Last Login</span>
                {getSortIcon('lastLogin')}
              </div>
            </th>
            <th 
              className="text-left p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort('createdAt')}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Created</span>
                {getSortIcon('createdAt')}
              </div>
            </th>
            <th className="text-left p-4">
              <span className="text-sm font-medium text-foreground">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {users?.map((user) => {
            const isCurrentUser = user?.id === currentUserId;
            
            return (
              <tr key={user?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Mail" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-foreground">{user?.email}</span>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-foreground">{user?.fullName || '-'}</span>
                </td>
                <td className="p-4">
                  {isCurrentUser ? (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${getRoleBadgeColor(user?.role)}`}>
                      {user?.role}
                    </span>
                  ) : (
                    <Select
                      options={roleOptions}
                      value={user?.role}
                      onChange={(newRole) => onRoleChange(user?.id, newRole)}
                      className="w-32"
                    />
                  )}
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">{formatDate(user?.lastLogin)}</span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">{formatDate(user?.createdAt)}</span>
                </td>
                <td className="p-4">
                  {!isCurrentUser && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onDeleteUser(user)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default UsersTable;